import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Button, Page, Tooltip } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { ErrorPage, NoInvoices } from '~/components/InvoicesEmptyStates/InvoicesEmptyStates';
import InvoicesTable from '~/components/InvoicesTable/InvoicesTable';
import PageLoadingState from '~/components/PageLoadingState';
import { useOverlayContext, useStore } from '~/contexts';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useLocaleKeys } from '~/i18n';
import { Intent } from '~/infra/access-control';
import { getAccountingDetails } from '~/integrations/Accounting/accounting.utils';
import { AccountingSyncIcon } from '~/integrations/Accounting/AccountingIcon/AccountingSyncIcon';
import { selectors } from '~/store/state-selectors';
import { Accounting } from '~/types/accounting';
import { ImportingInvoicesToQBOModal } from './ImportingInvoicesToQBOModal';
import styles from './InvoicesDashboard.module.scss';
export const GetInvoicesDashboardTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            InvoicesDashboardTID: {
                Loading: 'invoices-dashboard-loading',
                Root: 'invoices-dashboard',
                DownloadCsv: 'download-invoices-csv',
                ImportModal: 'importing-invoices-to-qbo-modal'
            }
        }
    });
export default function InvoicesDashboard() {
    const isVendor = useStore(selectors.user.isVendor);
    const isViewMode = useStore(selectors.isViewMode);
    const { t: invoiceDashTexts } = useLocaleKeys('pages.invoicesDashboard');
    const showQboIntegration = useStore(selectors.integration.showQboIntegration);
    const { showModal } = useOverlayContext();
    const pageTitle = invoiceDashTexts.pageTitle();
    const { InvoicesDashboardTID } = useMemo(()=>GetInvoicesDashboardTID(), []);
    useSetDocumentTitle(pageTitle);
    return /*#__PURE__*/ _jsx(InvoicesTable, {
        showMetrics: isVendor,
        filtersClassname: styles.filters,
        renderLoader: ()=>/*#__PURE__*/ _jsx(PageLoadingState, {
                header: pageTitle,
                loading: true,
                testId: InvoicesDashboardTID.Loading()
            }),
        renderError: (param)=>{
            let { refresh } = param;
            return /*#__PURE__*/ _jsx(ErrorPage, {
                title: pageTitle,
                retry: ()=>refresh()
            });
        },
        renderNoInvoices: (param)=>{
            let { refresh } = param;
            return /*#__PURE__*/ _jsx(NoInvoices, {
                title: pageTitle,
                isVendor: isVendor,
                onRefresh: refresh
            });
        },
        renderChild: (param)=>{
            let { tableContent, exportingCsv, exportCsv, isBackgroundLoading, refresh } = param;
            return /*#__PURE__*/ _jsx(Page, {
                testId: InvoicesDashboardTID.Root(),
                header: pageTitle,
                loading: isBackgroundLoading,
                onRefresh: refresh,
                mobile: {
                    hideSubheader: true
                },
                actions: /*#__PURE__*/ _jsx(Tooltip, {
                    title: isViewMode || showQboIntegration ? '' : isVendor ? "Export the invoices displayed on this page to a CSV file in the format required for ".concat(getAccountingDetails(Accounting.QBO).name) : 'Export the invoices displayed on this page to a CSV file',
                    placement: "bottom-end",
                    children: /*#__PURE__*/ _jsx("div", {
                        children: /*#__PURE__*/ _jsx(Button, {
                            bi: {
                                name: 'export_invoices_to_csv'
                            },
                            testId: InvoicesDashboardTID.DownloadCsv(),
                            skin: "primaryOutline",
                            size: "medium",
                            roundness: "m",
                            intent: Intent.InvoiceDownload,
                            loading: exportingCsv,
                            prefixIcon: showQboIntegration || !isVendor ? undefined : /*#__PURE__*/ _jsx(AccountingSyncIcon, {
                                prefix: "qbo"
                            }),
                            onClick: async ()=>{
                                exportCsv();
                                if (!showQboIntegration && isVendor) {
                                    await showModal({
                                        biName: 'importing_invoices_to_qbo_modal',
                                        content: (param)=>{
                                            let { dismissModal } = param;
                                            return /*#__PURE__*/ _jsx(ImportingInvoicesToQBOModal, {
                                                testId: InvoicesDashboardTID.ImportModal(),
                                                onClose: dismissModal,
                                                downloadCsv: exportCsv
                                            });
                                        }
                                    });
                                }
                            },
                            children: invoiceDashTexts.exportToCsv()
                        })
                    })
                }),
                children: tableContent
            });
        }
    });
}
